import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  coversApi,
  uploadToSignedUrl,
  type Cover,
  type CoverCategory,
  type TextZone,
} from '@/lib/api';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { CoverThumbnail } from '@/components/CoverThumbnail';
import type { CoverDesignTransform, TextZoneOverride } from '@/components/CoverThumbnail';
import { extractDominantColor, bakeDesignToPng } from '@/lib/imageUtils';
import { foldZoneScaleOverrides } from '@/lib/coverZoneBake';
import { TextZoneEditor } from './TextZoneEditor';

interface CoverEditDialogProps {
  cover: Cover;
  categories: CoverCategory[];
  onClose: () => void;
}

const FALLBACK_BG = '#f3f4f6';

export function CoverEditDialog({ cover, categories, onClose }: CoverEditDialogProps) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(cover.name);
  const [selectedCategories, setSelectedCategories] = useState<string[]>(cover.categories ?? []);
  const [isVisible, setIsVisible] = useState(cover.is_visible);

  function toggleCategory(slug: string) {
    setSelectedCategories((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]
    );
  }
  const [backCoverStyle, setBackCoverStyle] = useState<Cover['back_cover_style']>(
    cover.back_cover_style ?? 'solid'
  );
  const [textZones, setTextZones] = useState<TextZone[]>(cover.text_zones ?? []);
  const [dominantColor, setDominantColor] = useState<string>(cover.dominant_color ?? FALLBACK_BG);
  // Baseline tracks the "fresh" value of dominant color so the Annuler link
  // reverts to either the saved color OR the latest auto-extracted color after
  // a design replacement (whichever is more recent).
  const [bgBaseline, setBgBaseline] = useState<string>(cover.dominant_color ?? FALLBACK_BG);
  const [designUrl, setDesignUrl] = useState<string | null>(cover.design_url);
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(cover.thumbnail_url);
  const [storagePath, setStoragePath] = useState<string | null>(cover.storage_path);
  const [widthPx, setWidthPx] = useState<number | null>(cover.width_px);
  const [heightPx, setHeightPx] = useState<number | null>(cover.height_px);
  const [activeTab, setActiveTab] = useState<'info' | 'zones'>('info');
  const [replaceProgress, setReplaceProgress] = useState<number>(0);
  const replaceInputRef = useRef<HTMLInputElement>(null);
  const [designTransform, setDesignTransform] = useState<CoverDesignTransform>({ x: 0, y: 0, scale: 1 });
  const [designSelected, setDesignSelected] = useState(false);
  const designDirty =
    designTransform.x !== 0 || designTransform.y !== 0 || designTransform.scale !== 1;
  const [selectedZoneKey, setSelectedZoneKey] = useState<string | null>(null);
  const [editingZoneKey, setEditingZoneKey] = useState<string | null>(null);
  // Live corner-resize multipliers, kept OUT of the zone until save. Baking
  // `font_size × scale` on every mousemove compounds (text explodes/collapses);
  // holding scale as a transient override and folding once at save keeps the
  // resize tracking the cursor 1:1. Matches the customer cover editor's model.
  const [zoneOverrides, setZoneOverrides] = useState<Record<number, TextZoneOverride>>({});

  function applyOverrideToZone(i: number, ovr: TextZoneOverride) {
    // scale is a multiplier — never bake it per-move (it would compound). Keep
    // it live in zoneOverrides; foldZoneScaleOverrides collapses it at save.
    if (ovr.scale !== undefined) {
      setZoneOverrides((prev) => ({ ...prev, [i]: { ...prev[i], scale: ovr.scale } }));
    }
    // Everything else is an absolute value — safe to write straight into the zone.
    setTextZones((prev) => {
      const next = [...prev];
      const z = next[i];
      if (!z) return prev;
      const patch: Partial<TextZone> = {};
      if (ovr.x !== undefined) patch.x = ovr.x;
      if (ovr.y !== undefined) patch.y = ovr.y;
      if (ovr.color !== undefined) patch.color = ovr.color;
      if (ovr.fontFamily !== undefined) patch.font_family = ovr.fontFamily;
      if (ovr.fontSize !== undefined) patch.font_size = ovr.fontSize;
      if (Object.keys(patch).length === 0) return prev;
      next[i] = { ...z, ...patch };
      return next;
    });
  }

  function handleZoneTextChange(i: number, newText: string) {
    setTextZones((prev) => {
      const next = [...prev];
      if (!next[i]) return prev;
      next[i] = { ...next[i], default_text: newText };
      return next;
    });
  }

  function handleZoneDelete(i: number) {
    setTextZones((prev) => prev.filter((_, idx) => idx !== i));
    // Overrides are index-keyed, so deleting a zone must shift every key above
    // it down by one (and drop the deleted index) to stay aligned.
    setZoneOverrides((prev) => {
      const next: Record<number, TextZoneOverride> = {};
      for (const [k, v] of Object.entries(prev)) {
        const idx = Number(k);
        if (idx === i) continue;
        next[idx > i ? idx - 1 : idx] = v;
      }
      return next;
    });
    setSelectedZoneKey(null);
    setEditingZoneKey(null);
  }

  // After a replace, the cover always has a design_url even if it was legacy
  // before. Compute from current state, not the original cover prop.
  const canEditBg = !!designUrl;

  const save = useMutation({
    mutationFn: async () => {
      let finalDesignUrl = designUrl;
      let finalThumbnailUrl = thumbnailUrl;
      let finalStoragePath = storagePath;
      let finalWidth = widthPx;
      let finalHeight = heightPx;

      // Bake the admin's drag/resize into a fresh PNG so customers see the
      // design pre-positioned. Identity transform skips the bake.
      if (designDirty && designUrl) {
        const baked = await bakeDesignToPng(designUrl, designTransform);
        const bakedName = (name || 'cover').replace(/[^a-z0-9_-]+/gi, '_') + '_baked.png';
        const signed = await coversApi.getUploadUrl(bakedName, 'image/png');
        const bakedFile = new File([baked.blob], bakedName, { type: 'image/png' });
        await uploadToSignedUrl(signed.upload_url, bakedFile, 'image/png');
        finalDesignUrl = signed.public_url;
        finalThumbnailUrl = signed.public_url;
        finalStoragePath = signed.storage_path;
        finalWidth = baked.width;
        finalHeight = baked.height;
      }

      return coversApi.update(cover.id, {
        name,
        categories: selectedCategories,
        is_visible: isVisible,
        back_cover_style: backCoverStyle,
        // Collapse live resize multipliers into font_size exactly once.
        text_zones: foldZoneScaleOverrides(textZones, zoneOverrides),
        dominant_color: dominantColor,
        design_url: finalDesignUrl,
        thumbnail_url: finalThumbnailUrl,
        storage_path: finalStoragePath,
        width_px: finalWidth,
        height_px: finalHeight,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-covers'] });
      onClose();
    },
  });

  const replace = useMutation({
    mutationFn: async (file: File) => {
      setReplaceProgress(0);
      const signed = await coversApi.getUploadUrl(file.name, file.type);
      await uploadToSignedUrl(signed.upload_url, file, file.type, setReplaceProgress);
      let extractedColor: string | null = null;
      let extractedDims: { width: number; height: number } | null = null;
      if (file.type.startsWith('image/')) {
        try {
          const e = await extractDominantColor(file);
          extractedColor = e.color;
          extractedDims = { width: e.width, height: e.height };
        } catch {
          // non-fatal
        }
      }
      return { signed, extractedColor, extractedDims };
    },
    onSuccess: ({ signed, extractedColor, extractedDims }) => {
      setDesignUrl(signed.public_url);
      setThumbnailUrl(signed.public_url);
      setStoragePath(signed.storage_path);
      if (extractedColor) {
        setDominantColor(extractedColor);
        setBgBaseline(extractedColor);
      }
      if (extractedDims) {
        setWidthPx(extractedDims.width);
        setHeightPx(extractedDims.height);
      }
      // Fresh PNG starts at identity transform — old transform doesn't apply.
      setDesignTransform({ x: 0, y: 0, scale: 1 });
      setDesignSelected(false);
      setReplaceProgress(0);
    },
    onError: () => setReplaceProgress(0),
  });

  function handleReplaceClick() {
    replaceInputRef.current?.click();
  }

  function handleReplaceFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    replace.mutate(f);
  }

  const del = useMutation({
    mutationFn: () => coversApi.delete(cover.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-covers'] });
      onClose();
    },
  });

  function handleDelete() {
    if (confirm(`Supprimer "${cover.name}" définitivement ?`)) {
      del.mutate();
    }
  }

  return (
    <Modal isOpen={true} onClose={onClose} size="xl">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{cover.name}</h2>
          <p className="text-sm text-gray-500">Modifier la couverture</p>
        </div>
        <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500">
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      <div className="px-5 pt-3 border-b border-gray-100 flex gap-2">
        <TabButton active={activeTab === 'info'} onClick={() => setActiveTab('info')}>
          Informations
        </TabButton>
        <TabButton active={activeTab === 'zones'} onClick={() => setActiveTab('zones')}>
          Zones de texte
          {textZones.length > 0 && (
            <span className="ml-1.5 px-1.5 py-0.5 bg-gray-200 text-gray-700 rounded-full text-xs">
              {textZones.length}
            </span>
          )}
        </TabButton>
      </div>

      <div className="p-6">
        {activeTab === 'info' ? (
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Aperçu</label>
              <CoverThumbnail
                cover={{
                  ...cover,
                  dominant_color: dominantColor,
                  text_zones: textZones,
                  design_url: designUrl,
                  thumbnail_url: thumbnailUrl,
                }}
                className="rounded-xl border border-gray-200"
                bgColorOverride={dominantColor}
                interactive
                designTransform={designTransform}
                onDesignTransformChange={setDesignTransform}
                designSelected={designSelected}
                onDesignSelectedChange={setDesignSelected}
                destinationText={textZones.find((z) => z.type === 'destination')?.default_text ?? ''}
                yearText={textZones.find((z) => z.type === 'year')?.default_text ?? ''}
                customTexts={Object.fromEntries(
                  textZones.map((z, i) => [i, z.type === 'custom' ? (z.default_text ?? '') : ''])
                )}
                zoneOverrides={zoneOverrides}
                selectedZone={selectedZoneKey}
                editingZone={editingZoneKey}
                onZoneSelect={setSelectedZoneKey}
                onZoneEditChange={setEditingZoneKey}
                onZoneChange={applyOverrideToZone}
                onZoneTextChange={handleZoneTextChange}
                onZoneDelete={handleZoneDelete}
              />
              {widthPx && heightPx && (
                <p className="text-xs text-gray-400 mt-1">
                  Dimensions: {widthPx} × {heightPx} px
                </p>
              )}

              {designDirty && (
                <button
                  type="button"
                  onClick={() => setDesignTransform({ x: 0, y: 0, scale: 1 })}
                  className="mt-2 text-xs text-brand-500 hover:underline"
                >
                  Réinitialiser la position du design
                </button>
              )}

              <p className="text-xs text-gray-400 mt-2 leading-relaxed">
                <strong className="text-gray-600">Design :</strong> clic + glisser
                pour déplacer, roulette ou poignées d'angle pour redimensionner
                (position intégrée au PNG à l'enregistrement).
                <br />
                <strong className="text-gray-600">Texte :</strong> clic = sélection,
                glisser pour déplacer, double-clic pour éditer, poignées pour
                redimensionner.
              </p>

              <div className="mt-3 space-y-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleReplaceClick}
                  loading={replace.isPending}
                  className="w-full"
                >
                  {replace.isPending ? 'Téléversement…' : 'Remplacer le design PNG'}
                </Button>
                {replace.isPending && (
                  <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-brand-500 transition-all" style={{ width: `${replaceProgress}%` }} />
                  </div>
                )}
                {replace.isError && (
                  <p className="text-xs text-red-600">
                    Échec du remplacement. Réessayez.
                  </p>
                )}
                <input
                  ref={replaceInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  onChange={handleReplaceFile}
                  className="hidden"
                />
                <p className="text-xs text-gray-400 leading-relaxed">
                  PNG transparent recommandé. Le remplacement met aussi à jour
                  la couleur dominante extraite automatiquement.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <Field label="Nom">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </Field>

              <Field label="Catégories">
                <div className="grid grid-cols-2 gap-2">
                  {categories.map((cat) => (
                    <label
                      key={cat.slug}
                      className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={selectedCategories.includes(cat.slug)}
                        onChange={() => toggleCategory(cat.slug)}
                        className="w-4 h-4 accent-brand-500"
                      />
                      <span>{cat.icon} {cat.label_fr}</span>
                    </label>
                  ))}
                </div>
                {selectedCategories.length === 0 && (
                  <p className="text-xs text-red-600 mt-1.5">Sélectionnez au moins une catégorie.</p>
                )}
              </Field>

              <Field label="Couleur de fond">
                {canEditBg ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={dominantColor}
                      onChange={(e) => setDominantColor(e.target.value)}
                      className="w-12 h-9 rounded border border-gray-200 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={dominantColor}
                      onChange={(e) => setDominantColor(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono"
                    />
                    {dominantColor !== bgBaseline && (
                      <button
                        type="button"
                        onClick={() => setDominantColor(bgBaseline)}
                        className="text-xs text-gray-500 hover:text-gray-700 underline"
                      >
                        Annuler
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div
                      className="w-4 h-4 mt-0.5 rounded border border-gray-200 shrink-0"
                      style={{ backgroundColor: cover.dominant_color ?? FALLBACK_BG }}
                    />
                    <div className="text-xs text-gray-500">
                      <p>Cette couverture utilise une image de fond intégrée — la couleur ne peut pas être changée.</p>
                      {cover.dominant_color && (
                        <p className="font-mono mt-0.5">{cover.dominant_color}</p>
                      )}
                    </div>
                  </div>
                )}
              </Field>

              <Field label="Style du dos">
                <select
                  value={backCoverStyle ?? 'solid'}
                  onChange={(e) => setBackCoverStyle(e.target.value as Cover['back_cover_style'])}
                  className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white"
                >
                  <option value="solid">Couleur unie (couleur dominante)</option>
                  <option value="blank">Vierge (blanc)</option>
                  <option value="photo">Emplacement photo (client)</option>
                  <option value="mirrored">Design miroir (version claire)</option>
                </select>
              </Field>

              <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={isVisible}
                  onChange={(e) => setIsVisible(e.target.checked)}
                  className="w-4 h-4 accent-brand-500"
                />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">Visible pour les clients</p>
                  <p className="text-xs text-gray-500">
                    Les clients peuvent voir et choisir cette couverture
                  </p>
                </div>
              </label>
            </div>
          </div>
        ) : (
          <TextZoneEditor
            cover={{
              ...cover,
              dominant_color: dominantColor,
              design_url: designUrl,
              thumbnail_url: thumbnailUrl,
            }}
            backgroundColor={dominantColor}
            zones={textZones}
            onChange={setTextZones}
          />
        )}
      </div>

      <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between bg-gray-50">
        <Button variant="danger" size="sm" onClick={handleDelete} loading={del.isPending}>
          Supprimer
        </Button>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={onClose}>
            Annuler
          </Button>
          <Button
            onClick={() => save.mutate()}
            loading={save.isPending}
            disabled={selectedCategories.length === 0}
          >
            Enregistrer
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={
        'px-3 pb-2.5 -mb-px border-b-2 text-sm font-medium transition-colors ' +
        (active
          ? 'border-brand-500 text-brand-500'
          : 'border-transparent text-gray-500 hover:text-gray-700')
      }
    >
      {children}
    </button>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>
      {children}
    </div>
  );
}
